<script lang="ts">
  import { onMount } from 'svelte';

  import { api, ApiError } from './lib/api';
  import PairingScreen from './lib/PairingScreen.svelte';
  import Player from './lib/Player.svelte';
  import {
    clearAuthToken,
    getOrCreateDeviceId,
    loadAuthToken,
    saveAuthToken,
  } from './lib/storage';

  type State = 'booting' | 'pairing' | 'running' | 'error';

  let state: State = 'booting';
  let deviceId = '';
  let pairingCode = '';
  let pairingExpiresAt: string | null = null;
  let errorMessage = '';
  let pollHandle: ReturnType<typeof setTimeout> | null = null;

  onMount(async () => {
    deviceId = getOrCreateDeviceId();
    const token = loadAuthToken();
    if (token) {
      state = 'running';
    } else {
      void startPairing();
    }
  });

  async function startPairing() {
    state = 'booting';
    try {
      const res = await api.pairInit(deviceId);
      pairingCode = res.pairingCode;
      pairingExpiresAt = res.expiresAt;
      state = 'pairing';
      pollForClaim();
    } catch (err) {
      const e = err as ApiError;
      // Si el server dice "ya emparejada", reseteamos el token local y re-pareamos
      if (e.status === 409) {
        clearAuthToken();
      }
      errorMessage = e.message;
      state = 'error';
      // Retry suave en 10s
      setTimeout(() => startPairing(), 10_000);
    }
  }

  function pollForClaim() {
    if (pollHandle) clearTimeout(pollHandle);
    pollHandle = setTimeout(async () => {
      try {
        const res = await api.pairStatus(deviceId);
        if (res.paired && res.authToken) {
          saveAuthToken(res.authToken);
          state = 'running';
          return;
        }
        if (res.paired && !res.authToken) {
          // Token ya entregado previamente — el player debió guardarlo.
          // Si llegamos acá sin token guardado, algo se rompió. Reintentamos pair.
          if (!loadAuthToken()) {
            await startPairing();
            return;
          }
          state = 'running';
          return;
        }
      } catch (err) {
        // 404 = screen no existe → vuelve a pair-init para regenerar code
        if ((err as ApiError).status === 404) {
          await startPairing();
          return;
        }
        // Otros errores: log y reintentar
        // eslint-disable-next-line no-console
        console.warn('pair-status err', err);
      }
      // Volver a intentar en 4s
      pollForClaim();
    }, 4_000);
  }
</script>

{#if state === 'booting'}
  <div class="boot">
    <div class="spinner" />
    <p>Iniciando…</p>
  </div>
{:else if state === 'pairing'}
  <PairingScreen {pairingCode} {deviceId} expiresAt={pairingExpiresAt} />
{:else if state === 'running'}
  <Player />
{:else if state === 'error'}
  <div class="boot error">
    <p>Error: {errorMessage}</p>
    <small>Reintentando…</small>
  </div>
{/if}

<style>
  .boot {
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    color: #fff;
    background: #000;
  }
  .boot.error {
    background: #7f1d1d;
  }
  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(255, 255, 255, 0.2);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
</style>
